/**
 * Anchoring for Gantt comments — the glue between a bar on screen and a row in
 * the tenant's `SchedulerComments` table.
 *
 * A comment is stored against `(weekNumber, resourceType, resourceName,
 * productCode)` plus the window of the batch it was written on. **Only the
 * first four take part in matching.** `startTime`/`endTime` shift on every
 * re-run, so keying on them would orphan every comment the moment the planner
 * re-optimises; they are kept so the comment can say which batch it was
 * written against, and so the reader is told when that batch has since moved.
 *
 * Both Gantt implementations produce the same anchor — ScheduleFullGantt's
 * Timeline and By Product tabs, and FactoryScheduleView's TimelineTab — so a
 * comment added in one is found by the others. The anchor rides on the bar
 * rather than being derived from the row, because those views label the row
 * and the bar with opposite halves of the (resource, product) pair.
 *
 * Comments are partitioned by `{schedulerId}_{iteration}` server-side, so a
 * new version of a schedule starts with none.
 */

import { useEffect, useMemo, useState } from 'react'
import { getUserEmail } from 'Features/auth/auth'
import { SchedulerCommentAnchor } from './Scheduler.types'
import { SchedulerComment, useSchedulerComments } from './Scheduler.data'

/** An anchor plus the batch window it was taken from. */
export type CommentTarget = SchedulerCommentAnchor & {
  startTime: number
  endTime: number
  /** Shown in the popover header when the code alone isn't readable. */
  productDescription?: string
}

const norm = (s: string): string => (s ?? '').trim().toLowerCase()

/**
 * Match key for an anchor in a given week. Case- and whitespace-insensitive
 * throughout: resource and product names reach us from Delta rows and from
 * user-entered tables, and the two don't always agree on casing.
 *
 * The two kinds key on different things:
 *
 * - **production** — the resource, its side of the plant, and the product.
 * - **downtime** — the resource code alone. Nothing else on that row
 *   identifies the window: `resourceType` is a descriptive, editable column
 *   whose live values are inconsistent ('Line', 'line', 'Constraint'), so
 *   keying on it would orphan a comment the first time someone tidied one up,
 *   and there is no product.
 *
 * The kind is part of the key, so a downtime comment can never be picked up by
 * a production bar on the same resource that happens to carry no product code.
 */
export const commentAnchorKey = (
  a: SchedulerCommentAnchor,
  week: number
): string =>
  a.kind === 'downtime'
    ? `${week}|downtime|${norm(a.resourceName)}`
    : `${week}|production|${norm(a.resourceType)}|${norm(a.resourceName)}|${norm(a.productCode)}`

/** Match key for a stored comment, from its own recorded week. */
export const storedCommentKey = (c: SchedulerComment): string =>
  commentAnchorKey(c, c.weekNumber)

/**
 * The anchor for one resource's downtime in one week.
 *
 * **Storage is still per resource, and deliberately so**, even though the
 * editor now offers a thread per window. A downtime window has no identity to
 * key on — `InputWorkStationDownTimeDelta` holds one row per resource with
 * every window packed into a single `(8,12)(30,36)` string, and both parsers
 * hand back bare `[start, end]` pairs — so a key built from hours or from an
 * array index would orphan or mis-point a comment the first time someone
 * dragged or deleted a window.
 *
 * Instead the comment records the window it was written on in its existing
 * `startTime`/`endTime` fields, and `partitionDowntimeComments` assigns it to
 * a window by OVERLAP at read time. That survives a drag or a resize, keeps
 * the match key and the server contract unchanged, and leaves every comment
 * written before per-window threads existed (start=end=0) valid as a
 * resource-level note.
 */
export const downtimeAnchor = (
  resourceName: string,
  resourceType: string
): SchedulerCommentAnchor => ({
  resourceName,
  resourceType,
  productCode: '',
  kind: 'downtime',
})

/**
 * True when the batch this comment was written against is no longer where it
 * was — the stored window and the one now on screen don't overlap at all.
 * Used to caption the comment rather than to hide it: the anchor still
 * identifies the right product on the right resource, only the timing moved.
 *
 * A zero-length target window means the thread isn't anchored to one at all
 * (downtime is per resource, per week), so there is nothing to compare and
 * nothing to report.
 */
export const commentWindowMoved = (
  c: SchedulerComment,
  target: Pick<CommentTarget, 'startTime' | 'endTime'>
): boolean =>
  target.endTime > target.startTime &&
  (c.endTime <= target.startTime || c.startTime >= target.endTime)

/**
 * Split one resource's downtime comments across the windows currently on
 * screen, by overlap.
 *
 * Two things land in `resourceLevel` rather than on a window, and the
 * difference matters:
 *
 * - **A comment with no window** (`endTime <= startTime`, which is every
 *   comment written before per-window threads existed — they were stored 0/0).
 *   It was always a note about the resource being down; it stays one.
 * - **A comment whose window overlaps nothing any more.** Its window was
 *   deleted, or moved entirely clear of where it was written. Demoting it is
 *   what stops the original objection to per-window comments from biting: the
 *   note survives and stays readable instead of vanishing with the window.
 *
 * Overlap, not equality, is the whole point — a planner nudging 8–12 to 10–14
 * keeps the comment. Ties go to the earliest window so the result is stable
 * rather than dependent on iteration order.
 */
export const partitionDowntimeComments = (
  comments: readonly SchedulerComment[],
  windows: readonly (readonly [number, number])[]
): { resourceLevel: SchedulerComment[]; byWindow: SchedulerComment[][] } => {
  const byWindow: SchedulerComment[][] = windows.map(() => [])
  const resourceLevel: SchedulerComment[] = []

  for (const c of comments) {
    if (!(c.endTime > c.startTime)) {
      resourceLevel.push(c)
      continue
    }
    let bestIdx = -1
    let bestOverlap = 0
    windows.forEach(([ws, we], i) => {
      const overlap = Math.min(c.endTime, we) - Math.max(c.startTime, ws)
      if (overlap > bestOverlap) {
        bestOverlap = overlap
        bestIdx = i
      }
    })
    if (bestIdx >= 0) byWindow[bestIdx].push(c)
    else resourceLevel.push(c)
  }

  return { resourceLevel, byWindow }
}

export type CommentIndex = {
  /** Anchor key → its comments, newest first (the API sorts on write time). */
  byAnchor: Map<string, SchedulerComment[]>
  comments: SchedulerComment[]
  isLoading: boolean
  refetch: () => void
}

const EMPTY_INDEX: Map<string, SchedulerComment[]> = new Map()

/**
 * Every comment on one week of one schedule version, indexed by anchor so a
 * Gantt row can ask "does this bar have comments?" without a per-bar lookup
 * through the whole list. One query per week for the whole chart.
 *
 * `enabled` is what a caller without a schedule id passes — TimelineTab is
 * rendered in places that don't know one, and must degrade to no comments
 * rather than firing a request at `/scheduler/undefined/...`.
 */
export const useCommentIndex = (
  schedulerId: string | undefined,
  iteration: string | undefined,
  week: number
): CommentIndex => {
  const enabled = Boolean(schedulerId && iteration)
  const {
    data: comments,
    isLoading,
    refetch,
  } = useSchedulerComments(schedulerId ?? '', iteration ?? '', week, enabled)

  const byAnchor = useMemo(() => {
    if (!comments?.length) return EMPTY_INDEX
    const m = new Map<string, SchedulerComment[]>()
    for (const c of comments) {
      const key = storedCommentKey(c)
      const bucket = m.get(key)
      if (bucket) bucket.push(c)
      else m.set(key, [c])
    }
    return m
  }, [comments])

  return { byAnchor, comments: comments ?? [], isLoading, refetch }
}

/** Comments on one bar, or an empty array — never undefined, so callers can
 *  read `.length` without a guard on every bar in the chart.
 *
 *  `window` narrows a DOWNTIME bar to the comments written on that window —
 *  those overlapping it, plus any unwindowed resource-level note, which
 *  applies to the whole week and so to every window. Omit it to get every
 *  comment on the resource, which is what a per-row badge counting the whole
 *  thread wants.
 *
 *  Production is unaffected whether a window is passed or not: its anchor
 *  deliberately ignores times because the optimizer moves them on every run
 *  (see `SchedulerCommentAnchor`), and filtering on them here would orphan
 *  every production comment at the first re-run. That is why the narrowing is
 *  gated on `kind` rather than on whether a caller supplied a window — a
 *  caller passing a bar's hours for every bar must stay safe. */
export const commentsFor = (
  index: Map<string, SchedulerComment[]> | undefined,
  anchor: SchedulerCommentAnchor | undefined,
  week: number,
  window?: Pick<CommentTarget, 'startTime' | 'endTime'>
): SchedulerComment[] => {
  if (!anchor || !index?.size) return []
  const all = index.get(commentAnchorKey(anchor, week)) ?? []
  if (
    anchor.kind !== 'downtime' ||
    !window ||
    !(window.endTime > window.startTime)
  ) {
    return all
  }
  return all.filter(
    (c) =>
      !(c.endTime > c.startTime) ||
      Math.min(c.endTime, window.endTime) -
        Math.max(c.startTime, window.startTime) >
        0
  )
}

/**
 * The signed-in user's email, used to decide whether a comment is the reader's
 * own — which is what gates the delete affordance.
 *
 * Read from MSAL, not from `localStorage.userEmail`: nothing in the Portal
 * ever writes that key, so the read it replaces always returned '' and no one
 * could delete their own comment.
 */
export const useCurrentUserEmail = (): string => {
  const [email, setEmail] = useState('')
  useEffect(() => {
    let live = true
    getUserEmail()
      .then((e) => {
        if (live && e) setEmail(e)
      })
      .catch(() => {
        // Not signed in yet, or MSAL still settling — the delete affordance
        // simply stays hidden, which is the safe way to be wrong.
      })
    return () => {
      live = false
    }
  }, [])
  return email
}

// ─── Formatting shared by the comment surfaces ───────────────────────────────

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** Week-relative hour (0–167) as "Wed 14:00". */
export const formatWeekHour = (hour: number): string => {
  const day = DAY_LABELS[Math.floor(hour / 24) % 7]
  const h = Math.floor(hour % 24)
  return `${day} ${h.toString().padStart(2, '0')}:00`
}

export const commentInitials = (name: string): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return '??'
}

export const commentTimeAgo = (iso: string): string => {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const mins = Math.floor((Date.now() - then) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}
